/**
 * RecipeCard Component - REFACTORED
 * Badge-triggered accordion design
 *
 * Variants:
 * - meal-planning: Full features (Details, Customize, Re-Roll, Chat, Delete)
 * - view-only: Limited features (Details, Favorite, Notes)
 * - history: Recipe history with thumbs up/down, favorites, and date metadata
 */

import { useState, useEffect, useRef } from 'react'
import { useUserId, useSubscriptionWithLoading, useSubscription, recipeNotesService, RecipeNote, recipeChatService, type ChatMessage, type RecipeChatResult } from '@talktoast/shared'
import { RecipeDetailResponse } from '@talktoast/shared'
import { Card, Badge, Button, Loading } from '@talktoast/ui'
import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'
import RecipeDetailsPanel from './recipe/RecipeDetailsPanel'
import RecipeCustomizePanel from './recipe/RecipeCustomizePanel'
import RecipeChatPanel from './recipe/RecipeChatPanel'
import RecipeNotesPanel from './recipe/RecipeNotesPanel'
import { RecipeModifications } from '../types/recipe'

export interface Recipe {
  id: string
  name: string
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks' | 'dessert'
  protein?: string
  cookingMethod?: string
  cuisine?: string
}

type ActiveSection = 'details' | 'customize' | 'chat' | 'notes' | null

interface RecipeCardProps {
  recipe: Recipe
  recipeDetails?: RecipeDetailResponse | null
  isLoadingDetails?: boolean

  // Variant
  variant?: 'meal-planning' | 'view-only' | 'history'

  // History variant props
  thumbsRating?: -1 | 0 | 1
  isFavorite?: boolean
  dateUsed?: string
  description?: string
  prepTime?: number
  cookTime?: number
  servings?: number

  // Control expanded state externally (optional)
  isExpanded?: boolean
  onToggleExpanded?: (recipeId: string) => void

  // Meal Planning callbacks
  onReroll?: (recipeId: string, modifications?: RecipeModifications) => void
  onDelete?: (recipeId: string) => void
  onCustomize?: (recipeId: string, modifications: RecipeModifications) => void

  // View Only callbacks
  onToggleFavorite?: (recipeId: string) => void
  onSaveNote?: (recipeId: string, note: string) => Promise<void>
  onDeleteNote?: (recipeId: string, noteId: string) => Promise<void>

  // History variant callbacks
  onThumbsUp?: (recipeId: string) => void
  onThumbsDown?: (recipeId: string) => void

  // Chat callback (available in all variants, but primarily for meal-planning)
  onSendMessage?: (recipeId: string, message: string) => Promise<string>

  // Navigation callback for subscription upgrade CTA
  onNavigateToSubscription?: () => void

  // Deprecated (for backward compatibility during migration)
  onMealTypeFilter?: (mealType: string) => void
  onReplaceWithFavorite?: (recipeId: string, favoriteRecipeName: string) => void
}

// Meal type to emoji mapping
const mealTypeEmojis: Record<string, string> = {
  breakfast: '🍳',
  lunch: '🥗',
  dinner: '🍽️',
  snacks: '🍿',
  dessert: '🍰',
}

export default function RecipeCard({
  recipe,
  recipeDetails,
  isLoadingDetails = false,
  variant = 'meal-planning',
  thumbsRating = 0,
  isFavorite: isFavoriteProp = false,
  dateUsed,
  description,
  prepTime,
  cookTime,
  servings,
  isExpanded,
  onToggleExpanded,
  onReroll,
  onDelete,
  onCustomize,
  onToggleFavorite,
  onSaveNote,
  onDeleteNote,
  onThumbsUp,
  onThumbsDown,
  onSendMessage,
  onNavigateToSubscription,
}: RecipeCardProps) {
  const userId = useUserId()
  const { safeUsage, safeLimits, loading: subscriptionLoading } = useSubscriptionWithLoading()
  const { subscription: sub } = useSubscription()
  const isFreeTier = sub?.tier === 'free' || sub === null
  const [activeSection, setActiveSection] = useState<ActiveSection>(isExpanded ? 'details' : null)
  const [isFavorite, setIsFavorite] = useState(isFavoriteProp)
  const [isRerolling, setIsRerolling] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [currentNotes, setCurrentNotes] = useState<string | null>(null)

  // Ref to track the deferred reroll timer so it can be cancelled on unmount
  const rerollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear the deferred reroll timer when the component unmounts to prevent
  // setState calls on an unmounted component.
  useEffect(() => {
    return () => {
      if (rerollTimerRef.current !== null) {
        clearTimeout(rerollTimerRef.current)
      }
    }
  }, [])

  // Calculate global weekly re-roll stats
  // safeUsage and safeLimits never return null (guaranteed by useSubscriptionWithLoading)
  const rerollsUsed = safeUsage.weeklyRerolls?.used ?? 0
  const rerollsLimit = safeUsage.weeklyRerolls?.limit ?? safeLimits.weeklyRerolls // null = unlimited
  const unlimited = rerollsLimit === null
  const rerollsExceeded = !unlimited && rerollsUsed >= rerollsLimit!
  const rerollsPercentUsed = !unlimited && rerollsLimit ? (rerollsUsed / rerollsLimit) * 100 : 0
  const showWarning = rerollsPercentUsed >= 70

  // Sync internal state with external control
  useEffect(() => {
    if (isExpanded !== undefined) {
      setActiveSection(isExpanded ? 'details' : null)
    }
  }, [isExpanded])

  // Initialize notes from recipe details
  useEffect(() => {
    if (recipeDetails?.recipe) {
      // Notes are included in recipe record
      const recipeRecord = recipeDetails.recipe as any
      setCurrentNotes(recipeRecord.notes || null)
    }
  }, [recipeDetails])

  // Toggle section (clicking same section collapses, clicking different section switches)
  const handleToggleSection = (section: ActiveSection) => {
    // If external control is provided for 'details', notify parent
    if (section === 'details' && onToggleExpanded) {
      onToggleExpanded(recipe.id)
      // External control will update isExpanded, which syncs to activeSection via useEffect
    } else {
      // Internal state management - toggle section
      setActiveSection(activeSection === section ? null : section)
    }
  }

  // High-variance re-roll (top-level button in meal-planning mode)
  // MealPlanningChat.handleRerollRecipe is the single owner of subscription
  // refresh for the dice-button path. RecipeCard must NOT call refreshSubscription
  // here — doing so creates a double-refresh race and was causing the counter to
  // flicker or show stale data until the second request completed.
  const handleReroll = async () => {
    if (!onReroll) return

    setIsRerolling(true)
    try {
      await onReroll(recipe.id)
    } catch (error) {
      console.error('[RecipeCard] Re-roll failed:', error)
    } finally {
      setIsRerolling(false)
    }
  }

  // Apply customizations (triggers re-roll with modifications)
  // Same ownership rule as handleReroll above — subscription refresh is
  // handled by MealPlanningChat.handleRerollRecipe, not here.
  const handleApplyCustomizations = async (modifications: RecipeModifications) => {
    if (!onReroll) return

    setIsRerolling(true)
    try {
      await onReroll(recipe.id, modifications)
      setActiveSection(null) // Collapse after applying
    } catch (error) {
      console.error('[RecipeCard] Customize failed:', error)
    } finally {
      setIsRerolling(false)
    }
  }

  // Delete meal
  const handleDelete = async () => {
    if (!onDelete) return

    const confirmed = confirm(`Remove "${recipe.name}" from your meal plan?`)
    if (!confirmed) return

    setIsDeleting(true)
    try {
      await onDelete(recipe.id)
    } catch (error) {
      console.error('[RecipeCard] Delete failed:', error)
      setIsDeleting(false)
    }
  }

  // Toggle favorite
  const handleToggleFavorite = async () => {
    if (!onToggleFavorite) return

    try {
      await onToggleFavorite(recipe.id)
      setIsFavorite(!isFavorite)
    } catch (error) {
      console.error('[RecipeCard] Favorite toggle failed:', error)
    }
  }

  // Thumbs up (history variant)
  const handleThumbsUp = () => {
    if (!onThumbsUp) return
    onThumbsUp(recipe.id)
  }

  // Thumbs down (history variant)
  const handleThumbsDown = () => {
    if (!onThumbsDown) return
    onThumbsDown(recipe.id)
  }

  // Recipe chat
  const handleSendMessage = async (message: string, conversationHistory: ChatMessage[]): Promise<string> => {
    // If parent provides onSendMessage, use that (legacy path — no rerollTrigger support)
    if (onSendMessage) {
      return await onSendMessage(recipe.id, message)
    }

    // Otherwise, use recipeChatService directly
    if (!recipeDetails) {
      throw new Error('Recipe details not loaded. Please wait a moment and try again.')
    }

    try {
      const result: RecipeChatResult = await recipeChatService.sendMessage(
        recipe.id,
        message,
        recipeDetails,
        conversationHistory,
        userId || undefined // Pass userId for guest users
      )

      // If the AI detected a reroll action, trigger a full recipe regeneration.
      // We do this after returning the display message so the user sees the confirmation
      // before the card transitions to a loading state.
      if (result.rerollTrigger && onReroll) {
        const trigger = result.rerollTrigger
        // Defer reroll slightly so the chat message renders first.
        // Store the timer ID so it can be cancelled if the component unmounts.
        rerollTimerRef.current = setTimeout(() => {
          rerollTimerRef.current = null
          if (trigger.type === 'full_replace') {
            // User asked for a completely different dish — pass targetDish so
            // MealPlanningChat can build a "replace with X" reroll message.
            // Sanitize at the AI-to-app handoff point to prevent prompt injection.
            const safeDish = trigger.targetDish.replace(/[\r\n]/g, ' ').trim().slice(0, 100)
            onReroll(recipe.id, { targetDish: safeDish })
          } else {
            // ingredient_swap: regenerate the same dish with a different ingredient (protein, starch, etc.)
            onReroll(recipe.id, { protein: trigger.to })
          }
        }, 600)
      }

      return result.message
    } catch (error: any) {
      console.error('[RecipeCard] Chat error:', error)
      throw new Error(error.message || 'Failed to send message. Please try again.')
    }
  }

  // Save note
  const handleSaveNote = async (note: string) => {
    try {
      const updatedNotes = await recipeNotesService.updateNotes(recipe.id, note)
      setCurrentNotes(updatedNotes)
    } catch (error) {
      console.error('[RecipeCard] Failed to save note:', error)
      throw error
    }
  }

  // Clear note
  const handleClearNote = async () => {
    try {
      const updatedNotes = await recipeNotesService.updateNotes(recipe.id, '')
      setCurrentNotes(updatedNotes)
    } catch (error) {
      console.error('[RecipeCard] Failed to clear note:', error)
      throw error
    }
  }

  const mealEmoji = mealTypeEmojis[recipe.mealType] || '🍽️'

  return (
    <Card
      variant={variant === 'history' ? 'elevated' : 'outlined'}
      padding="none"
      className={`transition-all duration-200 ${isDeleting ? 'opacity-50' : ''} cursor-pointer hover:ring-2 hover:ring-toast-gold/60 hover:shadow-lg active:bg-gray-50`}
      onClick={() => handleToggleSection('details')}
    >
      {/* Header - TodayMealCard Style */}
      <div className="p-4">
        <div className="w-full"
        >
          <div className="flex flex-col gap-2">
            {/* Top row: Meal type (left) + metadata + actions (right) */}
            <div className="flex items-center justify-between gap-3">
              {/* Left: Meal type label */}
              <div className="flex items-center gap-1.5">
                <span className="text-sm">{mealEmoji}</span>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {recipe.mealType}
                </span>
              </div>

              {/* Right: Metadata + Actions */}
              <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                {/* Metadata */}
                {recipeDetails?.recipe && (recipeDetails.recipe.prepTime || recipeDetails.recipe.cookTime || recipeDetails.recipe.servings) && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    {recipeDetails.recipe.prepTime && (
                      <span className="flex items-center gap-0.5">
                        <ICONS.actions.time className={getIconSize('xs')} />
                        {recipeDetails.recipe.prepTime}m
                      </span>
                    )}
                    {recipeDetails.recipe.cookTime && (
                      <span className="flex items-center gap-0.5">
                        <ICONS.food.chef className={getIconSize('xs')} />
                        {recipeDetails.recipe.cookTime}m
                      </span>
                    )}
                    {recipeDetails.recipe.servings && (
                      <span className="flex items-center gap-0.5">
                        <ICONS.user.users className={getIconSize('xs')} />
                        {recipeDetails.recipe.servings}
                      </span>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  {/* Favorite (meal-planning + view-only only — history shows it in badges row) */}
                  {variant !== 'history' && (
                    <button
                      onClick={handleToggleFavorite}
                      disabled={!onToggleFavorite}
                      className={`p-2 rounded-lg transition-colors ${
                        isFavorite
                          ? 'text-pink-600 bg-pink-50 hover:bg-pink-100'
                          : 'text-gray-400 hover:text-pink-600 hover:bg-pink-50'
                      } ${!onToggleFavorite ? 'opacity-0 pointer-events-none' : ''}`}
                      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <ICONS.food.heart className={getIconSize('sm')} />
                    </button>
                  )}

                  {/* Re-Roll (meal-planning only) */}
                  {variant === 'meal-planning' && onReroll && (
                    <div className="flex flex-col items-center gap-0.5">
                      <button
                        onClick={rerollsExceeded ? undefined : handleReroll}
                        disabled={isRerolling || isLoadingDetails || subscriptionLoading || rerollsExceeded}
                        className={`p-2 rounded-lg transition-colors ${
                          rerollsExceeded
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-500 hover:text-toast-gold hover:bg-toast-gold/10'
                        } disabled:opacity-50`}
                        title={
                          subscriptionLoading
                            ? 'Loading...'
                            : rerollsExceeded
                              ? 'Re-roll limit reached'
                              : isFreeTier
                                ? 'Re-roll recipe (from our collection)'
                                : 'Re-roll recipe (AI-powered)'
                        }
                      >
                        {isRerolling || subscriptionLoading ? (
                          <Loading variant="spinner" size="sm" />
                        ) : rerollsExceeded ? (
                          <ICONS.actions.lock className={getIconSize('sm')} />
                        ) : (
                          <span className="text-base">🎲</span>
                        )}
                      </button>
                      {/* Free-tier label: subtle hint that rerolls come from the recipe DB */}
                      {isFreeTier && !rerollsExceeded && !subscriptionLoading && (
                        <span className="text-[10px] text-gray-400 leading-none">
                          From our collection
                        </span>
                      )}
                      {!unlimited && (
                        <span className={`text-[10px] font-medium ${
                          subscriptionLoading
                            ? 'text-gray-400'
                            : showWarning && !rerollsExceeded
                              ? 'text-coral-pop'
                              : 'text-gray-500'
                        }`}>
                          {subscriptionLoading
                            ? '...'
                            : `${rerollsUsed}/${rerollsLimit} this week`
                          }
                        </span>
                      )}
                      {rerollsExceeded && !subscriptionLoading && (
                        <button
                          className="text-[10px] font-medium text-toast-gold hover:underline"
                          onClick={(e) => { e.stopPropagation(); onNavigateToSubscription?.() }}
                        >
                          Upgrade
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom row: Recipe name (full width) */}
            <h3 className="text-base font-semibold text-gray-900 leading-snug line-clamp-2">
              {recipe.name}
            </h3>
          </div>
        </div>

        {/* Badges - Meal Planning Variant */}
        {variant === 'meal-planning' && (
          <div className="flex flex-wrap gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
            {/* Details (expand/collapse) */}
            <button
              onClick={() => handleToggleSection('details')}
              className={`
                px-3 py-1.5 rounded-full text-xs font-medium transition-all
                flex items-center gap-1.5 border-2
                ${activeSection === 'details'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                }
              `}
              aria-label={activeSection === 'details' ? 'Collapse recipe details' : 'Expand recipe details'}
            >
              {activeSection === 'details'
                ? <ICONS.actions.collapse className={getIconSize('xs')} />
                : <ICONS.actions.expand className={getIconSize('xs')} />
              }
              <span>Details</span>
            </button>

            {/* Customize */}
            <button
              onClick={() => handleToggleSection('customize')}
              className={`
                px-3 py-1.5 rounded-full text-xs font-medium transition-all
                flex items-center gap-1.5 border-2
                ${activeSection === 'customize'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                }
              `}
            >
              <ICONS.actions.edit className={getIconSize('xs')} />
              <span>Customize</span>
            </button>

            {/* Chat */}
            <button
              onClick={() => handleToggleSection('chat')}
              className={`
                px-3 py-1.5 rounded-full text-xs font-medium transition-all
                flex items-center gap-1.5 border-2
                ${activeSection === 'chat'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                }
              `}
            >
              <ICONS.actions.message className={getIconSize('xs')} />
              <span>Chat</span>
            </button>

            {/* Delete */}
            {onDelete && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 border-2 bg-white text-red-600 border-gray-200 hover:border-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {isDeleting ? (
                  <Loading variant="spinner" size="sm" />
                ) : (
                  <ICONS.actions.delete className={getIconSize('xs')} />
                )}
                <span>Remove</span>
              </button>
            )}
          </div>
        )}

        {/* Badges - View Only Variant */}
        {variant === 'view-only' && (
          <div className="flex flex-wrap gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
            {/* Details (expand/collapse) */}
            <button
              onClick={() => handleToggleSection('details')}
              className={`
                px-3 py-1.5 rounded-full text-xs font-medium transition-all
                flex items-center gap-1.5 border-2
                ${activeSection === 'details'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                }
              `}
              aria-label={activeSection === 'details' ? 'Collapse recipe details' : 'Expand recipe details'}
            >
              {activeSection === 'details'
                ? <ICONS.actions.collapse className={getIconSize('xs')} />
                : <ICONS.actions.expand className={getIconSize('xs')} />
              }
              <span>Details</span>
            </button>

            {/* Notes */}
            <button
              onClick={() => handleToggleSection('notes')}
              className={`
                p-2 rounded-full transition-all
                flex items-center justify-center border-2
                ${activeSection === 'notes'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                }
              `}
              title="Add notes"
            >
              <ICONS.actions.edit className={getIconSize('sm')} />
            </button>
          </div>
        )}

        {/* History Variant: Metadata Row (only show when collapsed) */}
        {variant === 'history' && activeSection !== 'details' && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-2 flex-wrap">
                {prepTime && (
                  <span className="flex items-center gap-1">
                    <ICONS.actions.time className={getIconSize('xs')} />
                    {prepTime}min prep
                  </span>
                )}
                {cookTime && (
                  <span className="flex items-center gap-1">
                    <ICONS.food.chef className={getIconSize('xs')} />
                    {cookTime}min cook
                  </span>
                )}
                {servings && (
                  <span>{servings} servings</span>
                )}
              </div>
              {dateUsed && (
                <span className="text-gray-400 whitespace-nowrap">
                  {new Date(dateUsed).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* History Variant Badges Row */}
        {variant === 'history' && (
          <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
            {/* Details (expand/collapse) */}
            <button
              onClick={() => handleToggleSection('details')}
              className={`
                px-3 py-1.5 rounded-full text-xs font-medium transition-all
                flex items-center gap-1.5 border-2
                ${activeSection === 'details'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                }
              `}
              aria-label={activeSection === 'details' ? 'Collapse recipe details' : 'Expand recipe details'}
            >
              {activeSection === 'details'
                ? <ICONS.actions.collapse className={getIconSize('xs')} />
                : <ICONS.actions.expand className={getIconSize('xs')} />
              }
              <span>Details</span>
            </button>

            {/* Thumbs Up */}
            <button
              onClick={handleThumbsUp}
              className={`
                p-2 rounded-full text-base transition-all
                flex items-center justify-center border-2
                ${thumbsRating === 1
                  ? 'bg-avocado-green text-white border-avocado-green'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-avocado-green'
                }
              `}
              title="Like this recipe"
            >
              <span>👍</span>
            </button>

            {/* Thumbs Down */}
            <button
              onClick={handleThumbsDown}
              className={`
                p-2 rounded-full text-base transition-all
                flex items-center justify-center border-2
                ${thumbsRating === -1
                  ? 'bg-coral-pop text-white border-coral-pop'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-coral-pop'
                }
              `}
              title="Dislike this recipe"
            >
              <span>👎</span>
            </button>

            {/* Favorite (history variant — co-located with thumbs up/down) */}
            {onToggleFavorite && (
              <button
                onClick={handleToggleFavorite}
                className={`
                  p-2 rounded-full transition-all
                  flex items-center justify-center border-2
                  ${isFavorite
                    ? 'bg-pink-100 text-pink-600 border-pink-300'
                    : 'bg-white text-gray-400 border-gray-200 hover:border-pink-300 hover:text-pink-600'
                  }
                `}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <ICONS.food.heart className={getIconSize('sm')} />
              </button>
            )}

            {/* Notes */}
            <button
              onClick={() => handleToggleSection('notes')}
              className={`
                p-2 rounded-full transition-all
                flex items-center justify-center border-2
                ${activeSection === 'notes'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
                }
              `}
              title="Add notes"
            >
              <ICONS.actions.edit className={getIconSize('sm')} />
            </button>

            {/* History Timeline (Coming Soon) */}
            <button
              disabled
              className="p-2 rounded-full transition-all flex items-center justify-center border-2 bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
              title="Coming soon: View all times you've cooked this recipe"
            >
              <ICONS.actions.time className={getIconSize('sm')} />
            </button>
          </div>
        )}
      </div>

      {/* Expandable Sections — stop propagation so clicks inside panels don't bubble to the card's toggle handler */}
      {(activeSection === 'details' || activeSection === 'customize' || activeSection === 'chat' || activeSection === 'notes') && (
        <div onClick={(e) => e.stopPropagation()}>
          {activeSection === 'details' && (
            <RecipeDetailsPanel
              recipeDetails={recipeDetails}
              isLoading={isLoadingDetails}
            />
          )}

          {activeSection === 'customize' && variant === 'meal-planning' && (
            <RecipeCustomizePanel
              currentProtein={recipe.protein}
              currentCuisine={recipe.cuisine}
              onApplyChanges={handleApplyCustomizations}
              isApplying={isRerolling}
            />
          )}

          {activeSection === 'chat' && (
            <RecipeChatPanel
              recipeId={recipe.id}
              recipeName={recipe.name}
              onSendMessage={handleSendMessage}
            />
          )}

          {activeSection === 'notes' && (variant === 'view-only' || variant === 'history') && (
            <RecipeNotesPanel
              recipeId={recipe.id}
              currentNote={currentNotes}
              onSaveNote={handleSaveNote}
              onClearNote={handleClearNote}
            />
          )}
        </div>
      )}
    </Card>
  )
}
